/**
 * dataLoader.js — Supabase CRUD for hologram_presets
 *
 * Handles both authenticated (edit mode) and anonymous (display mode) access.
 * Uses @supabase/supabase-js directly — no shared RaceProX packages.
 */
import { createClient as createSupabaseClient } from '@supabase/supabase-js';

const SUPABASE_URL =
  import.meta.env.VITE_SUPABASE_URL || 'https://yzeorxruvmotuhpregvk.supabase.co';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

/** @type {import('@supabase/supabase-js').SupabaseClient | null} */
let _client = null;

/**
 * Get or create the singleton Supabase client.
 * @returns {import('@supabase/supabase-js').SupabaseClient}
 */
export function createClient() {
  if (!_client) {
    _client = createSupabaseClient(SUPABASE_URL, SUPABASE_ANON_KEY);
  }
  return _client;
}

/**
 * Get the current authenticated user, or null if not logged in.
 */
export async function getSessionUser() {
  const supabase = createClient();
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error || !user) return null;
  return user;
}

/**
 * Load a preset by its ID (for edit mode — requires auth).
 * @param {string} presetId
 */
export async function loadPresetById(presetId) {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('hologram_presets')
    .select('*')
    .eq('id', presetId)
    .single();

  if (error) {
    console.error('[dataLoader] loadPresetById error:', error.message);
    return null;
  }
  return data;
}

/**
 * Load a preset by its public display token via RPC (F4).
 * Returns preset data + inline org branding — no organization_id exposed.
 * @param {string} token
 */
export async function loadPresetByToken(token) {
  const supabase = createClient();
  const { data, error } = await supabase.rpc('get_hologram_by_token', {
    lookup_token: token,
  });
  if (error || !data || data.length === 0) return null;
  return data[0];
}

/**
 * Upload an asset to Supabase Storage and return the storage path (F3).
 * The path (not URL) is stored in the DB. URLs are resolved at load time.
 * @param {string} orgId
 * @param {string} presetId
 * @param {string} fileName
 * @param {Blob} blob
 * @param {string} contentType
 * @returns {Promise<string>} The storage path
 */
export async function uploadHologramAsset(orgId, presetId, fileName, blob, contentType) {
  const supabase = createClient();
  const storagePath = `${orgId}/${presetId}/${fileName}`;
  const { error } = await supabase.storage
    .from('hologram-assets')
    .upload(storagePath, blob, { contentType, upsert: true });
  if (error) throw new Error(`Upload failed: ${error.message}`);
  return storagePath; // Store path, not URL
}

/**
 * Resolve a storage path to a public URL for loading (F3).
 * @param {string | null} storagePath
 * @returns {string | null}
 */
export function resolveStorageUrl(storagePath) {
  if (!storagePath) return null;
  const supabase = createClient();
  const { data } = supabase.storage
    .from('hologram-assets')
    .getPublicUrl(storagePath);
  return data?.publicUrl ?? null;
}

/**
 * Save (update) a preset to Supabase. Used in edit mode (F3).
 * @param {string} presetId
 * @param {object} payload - The DB-ready payload from storedPresetToDbPayload()
 */
export async function savePreset(presetId, payload) {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('hologram_presets')
    .update({
      visual_settings: payload.visual_settings,
      markers: payload.markers,
      marker_drivers: payload.marker_drivers,
      svg_storage_path: payload.svg_storage_path ?? null,
      map_storage_path: payload.map_storage_path ?? null,
      display_font_storage_path: payload.display_font_storage_path ?? null,
    })
    .eq('id', presetId)
    .select()
    .single();

  if (error) {
    console.error('[dataLoader] savePreset error:', error.message);
    throw new Error(`Failed to save preset: ${error.message}`);
  }
  return data;
}

/**
 * Build a StoredPreset-compatible object from a DB preset row (F8).
 * Maps the Supabase schema to the format expected by the hologram engine.
 * Settings mapping is LEFT UNTOUCHED — only driver metadata changes.
 * @param {object} dbPreset - Raw row from hologram_presets table
 * @returns {object} A StoredPreset-compatible object
 */
export function dbPresetToStoredPreset(dbPreset) {
  const vs = dbPreset.visual_settings || {};
  const markers = dbPreset.markers || {};
  const md = dbPreset.marker_drivers || {};

  return {
    version: 4,
    settings: {
      ...vs,
      // Per-instance settings from markers column (unchanged from current code)
      trackNameText: markers.trackNameText ?? vs.trackNameText ?? '',
      trackScale: markers.trackScale ?? vs.trackScale ?? 0.012,
      circuitFlip: markers.circuitFlip ?? vs.circuitFlip ?? true,
      circuitRotationY: markers.circuitRotationY ?? vs.circuitRotationY ?? 0,
      lapClockwise: markers.lapClockwise ?? vs.lapClockwise ?? true,
    },
    customCornerTs: markers.customCornerTs || [],
    excludedCornerTs: markers.excludedCornerTs || [],
    startMarkerT: markers.startMarkerT ?? null,
    cornerNames: markers.cornerNames || {},
    // F8: Unified — marker_drivers is source of truth
    markerDrivers: md,
    // F3: Storage paths (resolved to URLs at load time)
    svgStoragePath: dbPreset.svg_storage_path || null,
    mapStoragePath: dbPreset.map_storage_path || null,
    displayFontStoragePath: dbPreset.display_font_storage_path || null,
    svgFileLabel: markers.svgFileLabel || 'Circuit SVG',
    mapFileLabel: markers.mapFileLabel || null,
    overlayFontFileLabel: markers.displayFontFileLabel || null,
    venueCircuitId: dbPreset.circuit_id || null,
  };
}

/**
 * Build a DB update payload from the current hologram engine state (F8).
 * @param {object} snapshot - A StoredPreset-compatible snapshot from the engine
 * @returns {object} Payload suitable for savePreset()
 */
export function storedPresetToDbPayload(snapshot) {
  const settings = { ...snapshot.settings };
  // Extract per-instance fields into markers
  const perInstance = [
    'trackNameText', 'trackScale', 'circuitFlip', 'circuitRotationY', 'lapClockwise'
  ];
  const markers = {};
  for (const key of perInstance) {
    if (key in settings) {
      markers[key] = settings[key];
      delete settings[key];
    }
  }

  markers.customCornerTs = snapshot.customCornerTs || [];
  markers.excludedCornerTs = snapshot.excludedCornerTs || [];
  markers.startMarkerT = snapshot.startMarkerT ?? null;
  markers.cornerNames = snapshot.cornerNames || {};
  markers.svgFileLabel = snapshot.svgFileLabel || null;
  markers.mapFileLabel = snapshot.mapFileLabel || null;
  markers.displayFontFileLabel = snapshot.overlayFontFileLabel || null;

  return {
    visual_settings: settings,
    markers,
    marker_drivers: snapshot.markerDrivers || {},
    svg_storage_path: snapshot.svgStoragePath ?? null,
    map_storage_path: snapshot.mapStoragePath ?? null,
    display_font_storage_path: snapshot.displayFontStoragePath ?? null,
  };
}
